import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import type { HotGameWin } from '../ui-activity-lane/ui-activity-lane';
import style from './ui-win-history.css?inline';
import '../ui-win-card/ui-win-card';

/** Each item in our list, with its status and bounding box data for FLIP. */
type DisplayEntry = {
  item: HotGameWin;
  status: 'exit' | 'stay' | 'enter';
  order?: number;
  oldRect?: DOMRect;
};

const CName = 'ui-win-history';

const ANIMATION_DURATION = 300;
const ANIMATION_DELAY = 300;

@customElement(CName)
export class UiWinHistory extends LitElement {
  static readonly styles = unsafeCSS(style);
  @property({ type: Array }) items: HotGameWin[] = [];
  @property({ type: Boolean, reflect: true, attribute: 'reduce-motion' }) reduceMotion = false;
  @state() _items: HotGameWin[] = [];
  @state() _previousItems: HotGameWin[] = [];
  @state() _stickyItemId = '';
  @state() _exitingIds: string[] = [];

  /**
   * Holds the items currently in the DOM (or soon to be).
   * Keyed by item.id, storing status + bounding box, etc.
   */
  private _displayMap: Map<string, DisplayEntry> = new Map();

  /**
   * New items that we will only add in Phase 3 (animate in).
   */
  private _pendingEnters: HotGameWin[] = [];

  /**
   * The 3-phase sequence:
   *  1 => expired items drop out
   *  2 => leftover items reflow to new positions (FLIP)
   *  3 => new items drop in
   */
  private _phase: 1 | 2 | 3 = 1;

  /**
   * We'll store references to leftover items' DOM elements during Phase 2
   * so we can track when the reflow animations finish.
   */
  private _leftoverAnimations: Animation[] = [];

  protected override scheduleUpdate(): undefined | Promise<unknown> {
    if (!this.isConnected) return;
    return super.scheduleUpdate() as undefined | Promise<unknown>;
  }

  protected willUpdate(changedProps: Map<string, unknown>) {
    if (changedProps.has('items')) {
      this._checkIfLastItemTheSame();
      this._computeDiffs();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.setProperty('--animation-duration', `${ANIMATION_DURATION}ms`);
    this._previousItems = [...this.items];
    // Initialize _displayMap with the initial items as 'stay' (no initial animation).
    for (const it of this.items) {
      this._displayMap.set(it.id, { item: it, status: 'stay' });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Cancel all in-flight FLIP animations to release target element references
    for (const anim of this._leftoverAnimations) {
      anim.cancel();
    }
    this._leftoverAnimations = [];
    this._displayMap.clear();
    this._pendingEnters = [];
  }

  updated(changedProps: Map<string, unknown>) {
    super.updated(changedProps);

    if (changedProps.has('items') && this._phase !== 1) {
      // After the render, handle Phase 2 reflow if needed
      this.updateComplete.then(() => {
        if (!this.isConnected) return;
        if (this._phase === 2) {
          this._startLeftoverReflow();
        }
      });
    } else if (this._phase === 2) {
      this.updateComplete.then(() => {
        if (!this.isConnected) return;
        this._startLeftoverReflow();
      });
    }
  }

  /**
   * Renders the items in different statuses depending on the current phase.
   *
   * Statuses:
   * - 'exit' => .drop-out
   * - 'stay' => no direct class (FLIP movement in phase 2)
   * - 'enter' => .drop-in (phase 3)
   */
  render() {
    // Gather IDs in a final rendering order
    const exitIds: string[] = [];
    const exitIdsOrdered: { id: string; order: number }[] = [];
    const stayIds: string[] = [];
    const enterIds: string[] = [];

    for (const [id, entry] of this._displayMap.entries()) {
      if (entry.status === 'exit') exitIds.push(id);
      if (entry.status === 'exit') exitIdsOrdered.push({ id, order: entry.order || 0 });
      if (entry.status === 'stay') stayIds.push(id);
      if (entry.status === 'enter') enterIds.push(id);
    }

    const orderedExitIds = exitIdsOrdered.sort((a, b) => a.order - b.order).map((item) => item.id);
    const orderedStay = this.items.map((i) => i.id).filter((id) => stayIds.includes(id));

    // During Phase 1 & 2, leftover items + exit items are in the DOM
    let finalIds: string[] = [...orderedStay, ...orderedExitIds];
    let numberOfMovingItems = 3;

    if (this._stickyItemId) {
      const stayIdsWithoutSticky = orderedStay.filter((itemId) => itemId !== this._stickyItemId);
      finalIds = [...stayIdsWithoutSticky, ...orderedExitIds, this._stickyItemId];
    }

    // In case everything is exiting
    if (enterIds.length === 0 && stayIds.length === 0) {
      finalIds = [...orderedExitIds];
      numberOfMovingItems = 3;
    }

    // Phase 3: add enter items
    if (this._phase === 3) {
      finalIds = [...enterIds, ...orderedStay, ...exitIds];
      numberOfMovingItems = enterIds.length;
    }

    const containerClasses = classMap({
      'win-history__list': true,
      [`phase-${this._phase}`]: this._phase,
    });

    return html`
      <div class="win-history">
        <ul class=${containerClasses}>
          ${repeat(
            finalIds,
            (id) => id,
            (id, index) => {
              const entry = this._displayMap.get(id)!;
              const { item, status } = entry;

              const rowClasses = classMap({
                'hot-win__row': true,
                'hot-win__row--sticky-win': this._stickyItemId === id,
                'hot-win__row--drop-out': status === 'exit',
                'hot-win__row--stay': status === 'stay',
                'hot-win__row--drop-in': status === 'enter',
              });

              return html`
                <li
                  class=${rowClasses}
                  data-id=${id}
                  style="animation-delay: ${(numberOfMovingItems - index) * ANIMATION_DELAY}ms;"
                  @animationend=${(e: AnimationEvent) => this._onAnimationEnd(e, id, status)}
                >
                  <ui-win-card
                    .newlyEntered=${status === 'enter'}
                    .name=${item.name}
                    .amount="${item.winAmount}"
                    .multiplier="${item.multiplier}"
                  ></ui-win-card>
                </li>
              `;
            },
          )}
        </ul>
      </div>
    `;
  }

  private _checkIfLastItemTheSame() {
    const lastItem = this.items[this.items.length - 1];
    const prevLastItem = this._previousItems[this._previousItems.length - 1];
    this._stickyItemId = lastItem?.id === prevLastItem?.id ? lastItem!.id : '';
  }

  /**
   * Identify which items are expired, leftover, and new.
   * Then start Phase 1 if any items are 'exit'.
   * If no items are 'exit', skip straight to Phase 2, etc.
   */
  private _computeDiffs() {
    if (this.reduceMotion) {
      this._displayMap.clear();
      for (const item of this.items) {
        this._displayMap.set(item.id, { item, status: 'stay' });
      }
      this._previousItems = [...this.items];
      this._pendingEnters = [];
      this._phase = 1;
      this.requestUpdate();
      return;
    }

    const oldIds = new Set(this._displayMap.keys());
    const newIds = new Set(this.items.map((i) => i.id));

    let wasExitFound = false;
    let wasChangeFound = false;

    // Mark items not in the new list => 'exit'
    for (const id of oldIds) {
      if (!newIds.has(id)) {
        const entry = this._displayMap.get(id);
        if (entry) {
          entry.order = this._previousItems.findIndex((item) => item.id === id);
          entry.status = 'exit';
          wasExitFound = true;
          wasChangeFound = true;
        }
      }
    }

    // Collect new items for Phase 3
    this._pendingEnters = [];
    for (const newItem of this.items) {
      if (!this._displayMap.has(newItem.id)) {
        this._pendingEnters.push(newItem);
        wasChangeFound = true;
      } else {
        // leftover => 'stay'
        const entry = this._displayMap.get(newItem.id)!;
        if (entry.status !== 'exit') {
          entry.status = 'stay';
          entry.item = newItem; // update data if needed
        }
      }
    }

    if (!wasChangeFound) {
      // no changes detected; skipping animation phases
      return;
    }

    // Phase 1 (expired animate out) starts now
    this._phase = 1;
    this.requestUpdate();
    this._previousItems = [...this.items];

    // If no expired items, we can skip Phase 1 immediately
    // and go straight to Phase 2 after the next render.
    if (!wasExitFound) {
      this.updateComplete.then(() => {
        if (!this.isConnected) return;
        this._startPhase2();
      });
    }
  }

  /**
   * Phase 1 ends when all 'exit' animations are done => call _startPhase2().
   */
  private _startPhase2() {
    this._phase = 2;
    // Before reflow, measure leftover items oldRect
    this._measureOldPositions();

    this.requestUpdate();
  }

  /**
   * Now we animate leftover items from oldRect -> newRect.
   * When finished, we proceed to Phase 3.
   */
  private _startLeftoverReflow() {
    // Cancel any prior animations before starting new ones
    for (const anim of this._leftoverAnimations) {
      anim.cancel();
    }
    this._leftoverAnimations = [];

    const leftoverEls = this.renderRoot.querySelectorAll<HTMLElement>('.hot-win__row--stay');
    leftoverEls.forEach((el) => {
      const id = el.dataset.id;
      if (!id) return;

      const entry = this._displayMap.get(id);
      if (entry && entry.status === 'stay' && entry.oldRect) {
        const newRect = el.getBoundingClientRect();
        const dx = entry.oldRect.left - newRect.left;
        const dy = entry.oldRect.top - newRect.top;

        const anim = el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0,0)' }], {
          duration: ANIMATION_DURATION,
          easing: 'ease',
        });
        this._leftoverAnimations.push(anim);
      }
    });

    // Once all leftover animations are done, proceed to Phase 3
    if (this._leftoverAnimations.length > 0) {
      Promise.all(this._leftoverAnimations.map((a) => a.finished)).then(() => {
        if (!this.isConnected) return;
        this._leftoverAnimations = [];
        this._startPhase3();
      });
    } else {
      // No leftover animations => just jump to Phase 3
      this._startPhase3();
    }
  }

  /**
   * Phase 3: Add new items as 'enter' so they animate in from the top.
   */
  private _startPhase3() {
    this._phase = 3;
    // Insert new items with status = 'enter'
    for (const newItem of this._pendingEnters) {
      this._displayMap.set(newItem.id, { item: newItem, status: 'enter' });
    }
    this._pendingEnters = [];

    this.requestUpdate();
  }

  /**
   * During Phase 1, expired items have .drop-out.
   * On animationend, we remove them from the DOM.
   * When all exit items are gone, we start Phase 2.
   */
  private _onAnimationEnd(e: AnimationEvent, id: string, status: string) {
    if (status === 'exit' && e.animationName === 'drop-out') {
      this._displayMap.delete(id);
      this.requestUpdate();
      if (this._allExitsDone()) {
        this._startPhase2();
      }
    }
    if (status === 'enter' && e.animationName === 'drop-in') {
      // Once new items finish dropping in, mark them 'stay' if desired
      const entry = this._displayMap.get(id);
      if (entry) {
        entry.status = 'stay';
      }
    }
  }

  /**
   * Phase 1 ends when no items with status === 'exit' remain in _displayMap.
   */
  private _allExitsDone(): boolean {
    for (const e of this._displayMap.values()) {
      if (e.status === 'exit') {
        return false;
      }
    }
    return true;
  }

  /**
   * In Phase 2, leftover items reflow. For that, we store oldRect before re-render.
   */
  private _measureOldPositions() {
    this.renderRoot.querySelectorAll<HTMLElement>('.hot-win__row--stay').forEach((el) => {
      const id = el.dataset.id;
      if (!id) return;
      const entry = this._displayMap.get(id);
      if (entry && entry.status === 'stay') {
        entry.oldRect = el.getBoundingClientRect();
      }
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiWinHistory;
  }
}
